import React, { useState, useEffect, useRef } from "react";

import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import { isEmpty } from "lodash";
import { Trans, useTranslation } from "react-i18next";

import CardTable from "../../components/Tables/CardTable";
import Pagination from "../../components/Pagination/Pagination";
import OrganizationSubscriptionsAPI from "../../API/organizationSubscriptions.api";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { getCurrentDateYMD } from "../../utils/DateHelper";
import ActionsDropdown from "../../components/Dropdown/ActionsDropdown";
import BaseAlertModal from "../../components/Modals/BaseAlertModal";
import { getRoles } from "../../utils/OktaHelpers";
import { ROLES } from "../../utils/constants";
import { isAuthorized } from "../../utils/RolesHelpers";
import { longDateTime } from "../../utils/DateHelper";
import { getSessionFilters, getMappedFilters } from "../../utils/FilterHelpers";
import {
    durationFormatter,
    activeIconFormatter,
    statusFormatter,
} from "../../utils/TagsFormatters";
import { BOOLEAN_OPTIONS } from "../../utils/constants";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { SuccessAlert } from "../../utils/Alerts";
import { setObject, getObject } from "../../utils/StorageHelpers";
import MissingData from "../../components/Shared/MissingData";

const MySubscriptions = () => {
    const [curretUserRoles, setCurretUserRoles] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRendered, setIsRendered] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [filtersUsed, setFiltersUsed] = useState(false);
    const [filters, setFilters] = useState({});
    const [unsubscribeModal, setUnsubscribeModal] = useState(false);
    //React lazy initialization in to reduce the cost of useless calling of getSessionFilters
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("subscriptions-table-filters")
    );
    const [sortBy, setSortBy] = useState(undefined);
    const [sortDirection, setSortDirection] = useState(undefined);
    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Subscriptions-itemsPerPage", true) || 10
    );
    const { t } = useTranslation();

    const actionRow = useRef({});
    const AUTHORIZED_UNSUBSCRIBE = new Set(["Active", "Unpaid"]);

    const keys = [
        { name: "productName" },
        { name: "subscribedAt" },
        { name: "expiresAt" },
        { name: "boughtBy" },
        { name: "price" },
        {
            name: "duration",
            element: (row) => durationFormatter(row.duration),
        },
        {
            name: "active",
            element: (row) => activeIconFormatter(row.active),
        },
        {
            name: "status",
            element: (row) => statusFormatter(row.status),
        },
        {
            element: (row) => {
                return (
                    <ActionsDropdown
                        attributes={subscriptionActions(row)}
                        row={row}
                    />
                );
            },
        },
    ];

    const headers = [
        { label: t("productName"), name: "productName" },
        { label: t("subscriptionDate"), name: "subscribedAt" },
        { label: t("expirationDate"), name: "expiresAt" },
        { label: t("boughtBy"), name: "boughtBy", skipSort: true },
        { label: t("price"), name: "price" },
        { label: t("subscriptionPlan"), name: "duration", skipSort: true },
        { label: t("active"), name: "active", skipSort: true },
        { label: t("status"), name: "status", skipSort: true },
        {
            label: t("action"),
            skipSort: true,
            name: "action",
            roles: !isAuthorized(
                [ROLES.support, ROLES.partnerAdmin],
                curretUserRoles
            ),
        },
    ];

    const searchFilters = [
        {
            label: t("productName"),
            name: "productName",
            type: "Input",
            placeholder: "Si-ware Model",
        },
        {
            label: t("subscriptionDate"),
            name: "subscribedAt",
            type: "Date",
            max: getCurrentDateYMD(),
        },
        {
            label: t("expirationDate"),
            name: "expiresAt",
            type: "Date",
        },
        {
            label: t("active"),
            name: "active",
            type: "Dropdown",
            options: Object.values(BOOLEAN_OPTIONS).map((val) => ({
                label: t(val),
                value: val,
            })),
        },
    ];

    const subscriptionActions = (row) => [
        {
            name: t("unsubscribe"),
            icon: "fas fa-ban",
            authorized: isAuthorized(
                [ROLES.partnerAdmin, ROLES.support],
                getRoles()
            ),
            handler: () => unsubscribeHandler(row),
            color: "red",
            isDisabled: () => !AUTHORIZED_UNSUBSCRIBE.has(row.status),
        },
    ];

    useEffect(() => {
        const localStorageFilters = getSessionFilters(
            "organization-table-filters"
        );
        if (!isEmpty(localStorageFilters)) {
            setFilters(localStorageFilters);
            setAppliedFilters(localStorageFilters);
        }
        setIsRendered(true);
        setCurretUserRoles(getRoles());
    }, []);

    useEffect(() => {
        if (isRendered) {
            if (sortBy && sortDirection) {
                setCurrentPage(1);
                fetchData(appliedFilters, sortBy, sortDirection);
            } else {
                setCurrentPage(1);
                fetchData(appliedFilters);
            }
        }
    }, [sortBy, sortDirection, isRendered]);

    const applyFilters = (appiedFilters) => {
        fetchData(appiedFilters, sortBy, sortDirection);
        setFiltersUsed(true);
    };

    const fetchData = async (
        _filters = null,
        _sortBy = null,
        _sortDirection = null,
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsLoading(true);
        const newFilter = getMappedFilters(_filters, ["active"]);
        const _offset = offset < 0 ? 0 : offset; // to ensure you don't get a negative offset

        let result = await OrganizationSubscriptionsAPI.getMySubscriptions(
            itemsPerPage,
            newFilter,
            _offset,
            _sortBy,
            _sortDirection
        );

        setSubscriptions(mapSubscriptions(result?.data || []));
        setTotalCount(result?.totalCount || 0);
        setIsLoading(false);
    };

    const mapSubscriptions = (data) => {
        let mapped = [];
        if (data && data.length > 0) {
            mapped = data.map((subscription) => {
                return {
                    ...subscription,
                    expiresAt: longDateTime(subscription.expiresAt),
                    price: `$${subscription.price}`,
                    subscribedAt: longDateTime(subscription.subscribedAt),
                    active: subscription.active.toString(),
                };
            });
        }

        return mapped;
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            setIsLoading(true);
            // call API to get users with the next pagination
            fetchData(filters, sortBy, sortDirection, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Subscriptions-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        fetchData(filters, sortBy, sortDirection, 0, itemsPerPage);
    };

    const unsubscribeHandler = (model) => {
        setUnsubscribeModal(true);
        actionRow.current = { id: model.id, name: model.productName };
    };

    const unsubscribeModel = async () => {
        const subscriptionId = actionRow.current.id;
        if (subscriptionId) {
            const result = await OrganizationSubscriptionsAPI.unsubscribeModel(
                subscriptionId
            );
            if (result.modified) {
                SuccessAlert(t("unsubscriptionSuccess"));
                LogCapture.info(
                    `Unsubscribed successfully from subscription with id: ${subscriptionId}`
                );

                let newCurrentPage = currentPage;
                if (subscriptions.length <= 1 && currentPage > 1) {
                    newCurrentPage = currentPage - 1;
                }

                fetchData(filters, sortBy, sortDirection, newCurrentPage - 1);
                setCurrentPage(newCurrentPage);
            } else {
                LogCapture.error(
                    `Couldn't unsubscribe from id: ${subscriptionId}`
                );
            }
        }
    };

    return (
        <>
            <div id="customer-content" className="customer-content">
                <header className="main-content-header flex items-center justify-between">
                    <h1 className="h1">
                        <Trans i18nKey="mySubscriptions">
                            My Subscriptions
                        </Trans>
                    </h1>
                    <div className="action-header-wrapper flex items-center">
                        <FilterDropdown
                            filterOptions={searchFilters}
                            filters={filters}
                            setFilters={setFilters}
                            applyFilters={applyFilters}
                            component="subscriptions-table"
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                        />
                    </div>
                </header>
            </div>
            <LoadingOverlay active={isLoading} text="Loading...">
                <div className="card-table shadow">
                    {subscriptions?.length > 0 ? (
                        <>
                            <CardTable
                                headers={headers}
                                keys={keys}
                                rows={subscriptions}
                                purpose={"subscriptions"}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}
                            />
                            <Pagination
                                currentPage={currentPage}
                                setCurrentPage={handlePageClick}
                                totalCount={totalCount}
                                sizePerPage={itemsPerRequest}
                                handleItemsPerPage={handleItemsPerPage}
                            />
                        </>
                    ) : (
                        <MissingData
                            isFilterUsed={filtersUsed}
                            entity={t("subscriptions")}
                            includeText={!isLoading}
                            path="/store"
                        />
                    )}
                </div>
            </LoadingOverlay>

            <BaseAlertModal
                isVisible={unsubscribeModal}
                setIsVisible={setUnsubscribeModal}
                title={`${t("unsubscribeModalTitle")} ${
                    actionRow.current.name
                }?`}
                message={t("unsubscribeModalMessage")}
                action={{
                    submitAction: unsubscribeModel,
                }}
                type="warning"
            />
        </>
    );
};

export default MySubscriptions;
